/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/allowance-buckets.ts
 * FILE_ID: QB-AGGRID-ALLOWANCES-001
 * PURPOSE: Static catalogue + ColumnBand builder for the SAM-style
 *          allowance bucket columns (Gal / Prime / Paint / Lift /
 *          Transport / Fasteners / Polish / Misc). Each bucket is a
 *          currency column that stores its value in
 *          QuoteLineItem.custom_values[<bucketKey>] — zero backend
 *          change, the existing custom-column infrastructure handles
 *          read/write through valueGetter/valueSetter.
 * WHY: SAM lets estimators stash per-line cost allowances (extra
 *      paint, lifting gear, transport, fasteners, polish, misc) in
 *      named buckets that contribute to the row total. Quotech had
 *      no equivalent UI — estimators had to bury allowances inside
 *      labour_cost or material_cost or internal_notes. This band
 *      surfaces them as first-class columns, off by default to keep
 *      quote chrome minimal but discoverable via Setup → optional.
 * FLOW:
 *   ┌────────────────┐   ┌──────────────────────────┐   ┌─────────────────────┐
 *   │ ALLOWANCE_     │ → │ buildAllowanceBucketBand │ → │ ColumnBand          │
 *   │ BUCKETS const  │   │  (sets custom: true on   │   │  (consumed by       │
 *   │                │   │   each entry)            │   │   buildAgGridCol*)  │
 *   └────────────────┘   └──────────────────────────┘   └─────────────────────┘
 * RELATED:
 *   - scope-ag-grid-columns.ts — wires the band into the col-group tree
 *   - scope-sheet-grid/ScopeSheetGridTypes.ts — Column / ColumnBand types
 *   - lazy-aggrid-impl.tsx handleCellValueChanged — reads custom_values
 */

import type { Column, ColumnBand } from '../scope-sheet-grid/ScopeSheetGridTypes'

export interface AllowanceBucketDef {
  /** custom_values key used for persistence + columnId in the grid. */
  id: string
  /** Short header label shown in the AG-Grid column header. */
  label: string
  /** Default column width in px. */
  width: number
}

/**
 * The 8 SAM-style allowance buckets, in screen order. Order is part
 * of the contract — paste-from-Excel + fill-down + the Setup picker
 * all index by this list.
 */
export const ALLOWANCE_BUCKETS: ReadonlyArray<AllowanceBucketDef> = [
  { id: 'allowance_galvanise', label: 'Gal $', width: 70 },
  { id: 'allowance_prime', label: 'Prime $', width: 70 },
  { id: 'allowance_paint', label: 'Paint $', width: 70 },
  { id: 'allowance_lift', label: 'Lift $', width: 70 },
  { id: 'allowance_transport', label: 'Transport $', width: 95 },
  { id: 'allowance_fasteners', label: 'Fasteners $', width: 95 },
  { id: 'allowance_polish', label: 'Polish $', width: 75 },
  { id: 'allowance_misc', label: 'Misc $', width: 70 },
]

/** Set of allowance bucket ids — fast lookup for paste / fill-down. */
export const ALLOWANCE_BUCKET_IDS: ReadonlySet<string> = new Set(
  ALLOWANCE_BUCKETS.map((b) => b.id),
)

/**
 * Build the AG-Grid ColumnBand for the allowance buckets. Every entry
 * gets `custom: true` so the column factory routes its reads + writes
 * through QuoteLineItem.custom_values without requiring a backend
 * field or migration. Columns ARE marked `optional: true` (so the
 * existing Setup picker can hide them if estimators want a slimmer
 * grid) but the band itself ships VISIBLE by default — see
 * DEFAULT_VISIBLE_ALLOWANCE_IDS for the seeded "everything on"
 * preset that the AG-Grid column builder unions into
 * visibleOptionalIds for SAM-parity out of the box.
 */
export function buildAllowanceBucketBand(): ColumnBand {
  const columns: Column[] = ALLOWANCE_BUCKETS.map((b) => ({
    id: b.id,
    label: b.label,
    width: b.width,
    align: 'right',
    editable: true,
    optional: true,
    custom: true,
    type: 'currency',
  }))
  return {
    id: 'allowances',
    label: 'Allowances',
    color: 'orange',
    bgClass: 'bg-orange-50/50 dark:bg-orange-950/30',
    textClass: 'text-orange-700 dark:text-orange-400',
    columns,
  }
}

/**
 * SAM-style default: ALL allowance buckets visible out of the box.
 * Unioned with the per-quote visibleOptionalIds at column-build time
 * so the user keeps full Setup control — they can hide any bucket
 * they don't use without losing the others.
 */
export const DEFAULT_VISIBLE_ALLOWANCE_IDS: ReadonlyArray<string> =
  ALLOWANCE_BUCKETS.map((b) => b.id)
